/**
 * MetricCard — KPI display card with built-in HelpTip linking to the KPI
 * Definition Catalog (04B Section G).
 *
 * Phase 8 Prompt 2 — per audit deliverable 04B Section J + 04D Section M
 * React component contracts.
 *
 * Every analytics surface in 06A–G consumes this primitive. The `?` icon
 * opens a tooltip showing: definition + formula + "why this matters" +
 * benchmark range.
 *
 * Usage:
 *   <MetricCard kpi="email.open_rate" value={32.4} comparison="vs. 30.8 last week" />
 *   <MetricCard kpi="campaign.revenue_per_send" value="$0.81" trend="up" trendPct={5.2} />
 */

import React, { useMemo } from "react";
import { Box, Card, CardContent, Typography, Tooltip } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RemoveIcon from "@mui/icons-material/Remove";
import { useHelpContent } from "../../contexts/HelpContentContext";

export interface MetricCardProps {
  /** KPI id — looks up content.kpis[kpi]. */
  kpi: string;
  /** The actual metric value to display (already formatted). */
  value: string | number;
  /** Optional comparison line ("vs last week", "vs previous period"). */
  comparison?: string;
  /** Trend direction. */
  trend?: "up" | "down" | "flat";
  /** Trend percentage (positive number; sign comes from `trend`). */
  trendPct?: number;
  /**
   * Whether the trend direction is "good" (green) or "bad" (red).
   * For e.g. delivery rate going up = good (green ▲).
   * For e.g. unsubscribe rate going up = bad (red ▲).
   * If omitted, "up" is assumed good and "down" is assumed bad.
   */
  trendIsPositive?: boolean;
  /** Optional click handler (e.g. to drill into details). */
  onClick?: () => void;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  kpi,
  value,
  comparison,
  trend,
  trendPct,
  trendIsPositive,
  onClick,
}) => {
  const { getKpi } = useHelpContent();
  const entry = getKpi(kpi);

  const tooltipBody = useMemo(() => {
    if (!entry) return null;
    return (
      <Box sx={{ p: 0.5, maxWidth: 320 }}>
        <strong>{entry.label}</strong>
        <br />
        {entry.definition}
        <br />
        <br />
        <em style={{ fontSize: "0.85em" }}>Why this matters:</em>{" "}
        {entry.whyThisMatters}
        <br />
        <br />
        <em style={{ fontSize: "0.85em" }}>Formula:</em>{" "}
        <code style={{ fontSize: "0.85em" }}>{entry.formula}</code>
        {entry.benchmarkLow != null && entry.benchmarkHigh != null && (
          <>
            <br />
            <br />
            <em style={{ fontSize: "0.85em" }}>Benchmark:</em>{" "}
            {entry.benchmarkLow}
            {entry.unit ? entry.unit : "%"} – {entry.benchmarkHigh}
            {entry.unit ? entry.unit : "%"}
          </>
        )}
      </Box>
    );
  }, [entry]);

  // Render trend chip
  const renderTrend = () => {
    if (!trend) return null;
    const isGood =
      trendIsPositive ?? (trend === "up" ? true : trend === "down" ? false : true);
    const color = isGood ? "#146152" : "#7D1935";
    const Icon =
      trend === "up"
        ? ArrowUpwardIcon
        : trend === "down"
          ? ArrowDownwardIcon
          : RemoveIcon;
    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          color,
          fontSize: "0.85rem",
          fontWeight: 600,
          mt: 0.5,
        }}
      >
        <Icon sx={{ fontSize: 16, mr: 0.3 }} />
        {trendPct != null && <>{trendPct.toFixed(1)}%</>}
        {comparison && (
          <Box component="span" sx={{ ml: 0.6, color: "text.secondary", fontWeight: 400 }}>
            {comparison}
          </Box>
        )}
      </Box>
    );
  };

  // Fallback render if KPI entry is missing
  const label = entry?.label ?? kpi;

  return (
    <Card
      onClick={onClick}
      sx={{
        position: "relative",
        cursor: onClick ? "pointer" : "default",
        borderRadius: 2,
        boxShadow: "0 2px 5px rgba(0,0,0,0.04)",
        "&:hover": onClick
          ? { boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }
          : undefined,
        transition: "box-shadow 200ms",
      }}
    >
      <CardContent sx={{ position: "relative", "&:last-child": { pb: 2 } }}>
        {/* Help icon top-right */}
        {tooltipBody && (
          <Tooltip title={tooltipBody} placement="top" arrow>
            <Box
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                color: "text.secondary",
                cursor: "help",
                display: "flex",
                "&:hover": { color: "primary.main" },
              }}
              aria-label={`Help: ${label}`}
            >
              <HelpOutlineIcon sx={{ fontSize: 16 }} />
            </Box>
          </Tooltip>
        )}

        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            fontFamily: '"Montserrat", sans-serif',
            fontSize: "0.8rem",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            mb: 0.5,
          }}
        >
          {label}
        </Typography>

        <Typography
          variant="h4"
          sx={{
            fontFamily: '"Playfair Display", serif',
            fontWeight: 700,
            color: "#1A5F7A",
            lineHeight: 1.1,
          }}
        >
          {value}
        </Typography>

        {renderTrend()}
      </CardContent>
    </Card>
  );
};

export default MetricCard;
